<template>
  <main class="h-full w-full">
    <EditorDeck :deck-id="deckId" :init-slide="initSlide" :uuid="uuid" />
  </main>
</template>

<script lang="ts" setup>
import { DeckBaseAccess, DeckPermissionsUserRole } from '~~/entities/deck'

definePageMeta({ layout: 'isolated-page' })

const router = useRouter()
const route = useRoute()
const { state: userState, hasRole, setSelectedCompany } = useUser()
const { getDeckPermissions } = useDeckPermissions()

if (!+route.params.id) {
  const res = /(\d+).*/.exec(`${route.params.id}`)
  if (res?.[1]) {
    navigateTo({ name: 'editor-decks-id', params: { id: res[1] }, replace: true })
  }
}

const deckId = `${route.params.id}`
const initSlide = route.query.slide ? `${route.query.slide}` : ''
const uuid = `${route.query.auth}`

const useDeckInterface = useDeck({
  id: toRef(route.params, 'id') as Ref<string>,
  user: ref(userState.value.user),
})
const { deck } = useDeckInterface

async function shouldRedirectToViewer() {
  if (!deck.value?.id) return false

  // Internal & Super Admins should not be redirected to viewer
  if (hasRole('internal_admin') || hasRole('super_admin')) {
    return false
  }

  const hasPresentationsPermission = userState.value.user?.permissions?.some(permission => permission.permission_flag === 'presentations')
  if (!hasPresentationsPermission) {
    return true
  }

  const userIsAtCompany = userState.value.user?.companies.some(company => company.id === deck.value?.company_id)

  // Get deck permissions and check if user belongs to company
  const deckPermissions = await getDeckPermissions({
    deckId: deck.value?.id,
    companyId: deck.value?.company_id,
  })

  // If user belongs to company and deck has company edit or public access, no redirect
  if (
    userIsAtCompany &&
    (deckPermissions?.base_access === DeckBaseAccess.CompanyEdit || deckPermissions?.base_access === DeckBaseAccess.Public)
  ) {
    return false
  }

  // If user belongs to company and is not assigned to deck and deck has company view access, redirect to viewer
  const userAssignedToDeck = deckPermissions?.roles.some(role => role.user_id === userState.value.user?.id)
  if (userIsAtCompany && !userAssignedToDeck && deckPermissions?.base_access === DeckBaseAccess.CompanyView) {
    return true
  }

  // Invited users only, check if user is viewer and redirect
  const userIsViewer =
    deckPermissions?.roles.some(role => {
      return role.user_id === userState.value.user?.id && role.role === DeckPermissionsUserRole.Viewer
    }) ?? false
  return userIsViewer
}

watch(deck, async newDeck => {
  if (!newDeck) return

  setSelectedCompany(newDeck.company_id)

  if (await shouldRedirectToViewer()) {
    router.push(`/decks/${newDeck.id}`)
  }
})
</script>
